import { useLingui } from '@lingui/react/macro';

import AlertBar from '@/design_system/AlertBar';
import Button from '@/design_system/Button';
import { RadioItem, RadioItemGroup } from '@/design_system/Radio/RadioItem/RadioItem';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import IconWarning from '@/icons/Warning.svg';
import {
  ArticleConditionForm,
  ArticleConditionFormType,
  useUpdateArticleCondition,
} from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useDatabaseTranslation } from '@/services/i18n';
import useViewPort from '@/utils/useViewport';

import { ConditionFormQuestion, useQuestionErrors } from './ConditionQuestion';

export const ConditionQuestionRadio = ({
  question,
  parentQuestion,
  type,
  articleConditionForms,
}: {
  question: Extract<ConditionFormQuestion, { type: 'radio' }>;
  parentQuestion?: ConditionFormQuestion;
  type: ArticleConditionFormType;
  articleConditionForms: ArticleConditionForm[];
}) => {
  const { isMobile } = useViewPort();
  const { _db } = useDatabaseTranslation();
  const { article, view, initialConditionFormData, qualityControlFormData } = useArticleContext();
  const { t } = useLingui();

  const conditionForm =
    type === 'initial-condition'
      ? initialConditionFormData?.conditionForm
      : qualityControlFormData?.conditionForm;

  const errors = useQuestionErrors({ question, parentQuestion, type });
  const { mutateAsync: updateArticleCondition, isPending: isUpdateArticleCondition } =
    useUpdateArticleCondition({
      requestId: article.requestId,
      articleId: article.id,
      conditionForm,
    });

  const editableArticleConditionForm = articleConditionForms.find(
    (articleConditionForm) => !articleConditionForm.completedAt
  );

  const completedArticleConditionForms = articleConditionForms.filter(
    (articleConditionForm) => !!articleConditionForm.completedAt
  );

  const editable =
    !!editableArticleConditionForm &&
    (type === 'initial-condition' ? view.initialCondition.editable : view.qualityControl.editable);

  const articleConditionForm =
    editableArticleConditionForm ?? completedArticleConditionForms.at(-1);

  const selectedAnswerId =
    articleConditionForm?.articleConditions.find(
      (articleCondition) => articleCondition.questionId === question.id
    )?.answerId ?? null;

  const handleChange = (newValue: string) => {
    const newlySelectedAnswer = question.answers?.find((answer) => answer.id === newValue);

    if (newlySelectedAnswer) {
      updateArticleCondition({
        questionId: question.id,
        answers: [{ id: newlySelectedAnswer.id }],
      });
    }
  };

  const hasBlockingAnswer =
    type === 'quality-control' &&
    !!question?.answers?.find((answer) => selectedAnswerId === answer.id)?.makesQualityControlKO;

  const tooltipText = editable
    ? t({
        id: 'components.condition-question-radio.quality-control.ko.warning.message.edit',
        message:
          'Answering "No" marks quality control as failed and requires photos and comments to be added',
      })
    : t({
        id: 'components.condition-question-radio.quality-control.ko.warning.message.read',
        message: 'Quality control failed due to this answer',
      });

  return (
    <Stack gap={editable ? '0.5rem' : undefined} ariaLabel={_db(question.title)}>
      <Stack row gap="0.5rem">
        {hasBlockingAnswer && (!isMobile || (isMobile && !editable)) && (
          <Tooltip content={tooltipText} atStart>
            <Button variant="style-less">
              <IconWarning
                style={{
                  fontSize: '1rem',
                  color: '#B25E09',
                  marginTop: isMobile ? '2px' : '0',
                }}
                className="text-disabled"
              />
            </Button>
          </Tooltip>
        )}
        <span className="label-100-medium">{_db(question.title)}</span>
      </Stack>
      {editable && (
        <>
          <RadioItemGroup
            direction={isMobile ? 'column' : 'row'}
            value={selectedAnswerId}
            onChange={handleChange}
            name={_db(question.title)}
            isInvalid={errors?.noAnswers}
            disabled={isUpdateArticleCondition}
          >
            {question.answers?.map((answer) => {
              const label = _db(answer.text);

              return (
                <RadioItem key={answer.id} value={answer.id} ariaLabel={label}>
                  {label}
                </RadioItem>
              );
            })}
          </RadioItemGroup>
          {hasBlockingAnswer && isMobile && (
            <AlertBar type="warning" size="small" title={tooltipText} />
          )}
        </>
      )}
    </Stack>
  );
};
